import { Box, Confetti, Home, Pin, TruckSmall } from '@coolblue-development/icons';
import type { CSSProperties } from 'react';

// Muted, low-contrast palette so the graphic reads as a background layer.
const COLOR = {
  line: '#AFC4DA',
  blue: '#0090e3'
};

// Fixed coordinate space (matches the SVG viewBox) so the icons and the
// truck's motion path share one coordinate system and stay aligned.
const STAGE_W = 760;
const STAGE_H = 320;

// The swirling line: starts bottom-left, loops a few times, ends top-right.
// Reused for both the visible dotted stroke and the truck's motion path.
const SWIRL_PATH =
  'M 70 275 ' +
  'C 160 250, 210 180, 150 150 ' +
  'C 95 122, 120 70, 190 95 ' +
  'C 250 116, 250 210, 330 205 ' +
  'C 415 200, 405 105, 480 120 ' +
  'C 545 133, 520 205, 590 200 ' +
  'C 645 196, 655 130, 690 110';

const container: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  overflow: 'hidden',
  pointerEvents: 'none',
  userSelect: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

const stage: CSSProperties = {
  position: 'relative',
  width: STAGE_W,
  height: STAGE_H,
  transform: 'scale(1.5)',
  transformOrigin: 'center'
};

const at = (x: number, y: number, extra?: CSSProperties): CSSProperties => ({
  position: 'absolute',
  left: x,
  top: y,
  transform: 'translate(-50%, -50%)',
  ...extra
});

export const MovedBackground = (): JSX.Element => {
  return (
    <div style={container} aria-hidden="true">
      <style>
        {`
          @keyframes moved-travel {
            0% { offset-distance: 10%; transform: scale(1); }
            50% { transform: scale(0.9); }
            80% { transform: scale(0.68); }
            100% { offset-distance: 100%; transform: scale(0); }
          }
          @keyframes moved-bob {
            0%, 100% { transform: translate(-50%, -50%); }
            50% { transform: translate(-50%, calc(-50% - 6px)); }
          }
          @keyframes moved-drift {
            0%, 100% { transform: translate(-50%, -50%) rotate(-4deg); }
            50% { transform: translate(-50%, -50%) rotate(4deg); }
          }
          .moved-truck {
            offset-path: path('${SWIRL_PATH}');
            offset-rotate: auto;
            animation: moved-travel 9s linear infinite;
          }
          @media (prefers-reduced-motion: reduce) {
            .moved-anim, .moved-truck { animation: none !important; }
          }
        `}
      </style>

      <div style={stage}>
        {/* The swirling dotted line */}
        <svg
          width={STAGE_W}
          height={STAGE_H}
          viewBox={`0 0 ${STAGE_W} ${STAGE_H}`}
          fill="none"
          style={{ position: 'absolute', inset: 0 }}
        >
          <path d={SWIRL_PATH} stroke={COLOR.line} strokeWidth={3} strokeLinecap="round" strokeDasharray="1 12" />
        </svg>

        {/* Start: house + box, bottom-left */}
        <div style={at(78, 248)}>
          <Home width={112} height={112} fill={COLOR.blue} />
        </div>
        <div style={at(150, 288)}>
          <Box width={52} height={52} fill={COLOR.blue} />
        </div>

        {/* End: location pin (bottom on path end) + confetti cannon below it */}
        <div className="moved-anim" style={at(690, 72, { animation: 'moved-bob 3s ease-in-out infinite' })}>
          <Pin width={76} height={76} fill={COLOR.blue} />
        </div>
        <div className="moved-anim" style={at(724, 118, { animation: 'moved-drift 4s ease-in-out infinite' })}>
          <Confetti width={44} height={44} fill={COLOR.blue} />
        </div>

        {/* The van travelling along the swirling line */}
        <div className="moved-truck" style={{ position: 'absolute', top: 0, left: 0 }}>
          <TruckSmall width={72} height={72} fill={COLOR.blue} />
        </div>
      </div>
    </div>
  );
};
